import React from "react";
import { Camera, GitMerge, ScanFace, Sparkles, Check } from "lucide-react";
import { Reveal } from "./motion";

const flows = [
  {
    icon: Camera,
    tag: "Cadastro com Talos Vision",
    title: "Uma foto. Um produto no ar.",
    desc:
      "Fotografe o produto ou o código de barras e o Talos reconhece na hora — nome, marca, categoria e todo o enquadramento fiscal sugerido automaticamente. Sem conexão? O formulário continua editável em modo manual.",
    highlights: [
      "OCR de código de barras direto da câmera",
      "Nome, marca e categoria preenchidos por IA",
      "Sugestão fiscal completa (NCM, CEST, CFOP, IBS/CBS)",
      "Funciona offline em modo manual",
    ],
    media: "/assets/muft/cadastro_produto.png",
    mediaType: "image",
  },
  {
    icon: GitMerge,
    tag: "Motor Fiscal Inteligente",
    title: "Cada empresa ensina. Todo mundo ganha.",
    desc:
      "Quando uma NF-e é autorizada com sucesso, o Talos guarda essa classificação vinculada ao código de barras do produto. Ao cadastrar o mesmo item, você recebe a sugestão pronta — validada pelo mercado, com sigilo absoluto dos dados comerciais.",
    highlights: [
      "Sugestão fiscal treinada em emissões reais",
      "Vinculada ao código de barras (EAN)",
      "Privacidade total: dados comerciais isolados",
      "Precisão maior a cada NF-e autorizada",
    ],
    media: "/assets/muft/config_fiscal.jpeg",
    mediaType: "phone",
  },
  {
    icon: ScanFace,
    tag: "Ponto Facial & Geolocalizado",
    title: "Ponto com selfie, GPS e auditoria do Talos.",
    desc:
      "Cada marcação é verificada por biometria facial e GPS. Divergência de horário ou local? O Talos gera um resumo em português para o gestor decidir — sem perder tempo abrindo relatórios manuais.",
    highlights: [
      "Verificação facial instantânea na selfie",
      "Cruzamento com geolocalização em tempo real",
      "Resumo de divergências pronto pro gestor",
      "Conforme portaria 671 do MTP",
    ],
    media: "/assets/muft/ponto_digital.jpeg",
    mediaType: "phone",
  },
];

export const AIWorkflows = () => (
  <section
    id="ia-embutida"
    className="relative bg-ice py-24 transition-colors duration-500 dark:bg-navy-900 md:py-32"
    data-testid="ai-workflows-section"
  >
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <Reveal className="mx-auto mb-16 max-w-3xl text-center">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-navy-100 bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-navy-800 shadow-sm dark:border-white/10 dark:bg-navy-800 dark:text-lavender">
          <Sparkles className="h-3.5 w-3.5" /> IA Integrada aos Processos
        </div>
        <h2 className="text-4xl font-black tracking-tight text-navy-800 dark:text-white sm:text-5xl">
          IA nos fluxos reais da sua empresa
        </h2>
        <p className="mt-5 text-lg font-semibold text-navy-400 dark:text-navy-100/70">
          Veja como o Talos trabalha nos bastidores para economizar horas da sua rotina diária.
        </p>
      </Reveal>

      <div className="space-y-16 lg:space-y-24">
        {flows.map((f, i) => {
          const Icon = f.icon;
          const isOdd = i % 2 === 1;
          return (
            <Reveal
              key={f.tag}
              delay={0.1}
              className={`grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-16 ${
                isOdd ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              <div className="lg:col-span-6 space-y-6">
                <div className="inline-flex items-center gap-2 rounded-full bg-lavender/50 dark:bg-white/10 px-3.5 py-1.5 text-xs font-bold text-navy-800 dark:text-lavender">
                  <Icon className="h-4 w-4" />
                  <span>{f.tag}</span>
                </div>
                <h3 className="text-3xl font-black text-navy-800 dark:text-white sm:text-4xl">
                  {f.title}
                </h3>
                <p className="text-base font-medium leading-relaxed text-navy-400 dark:text-navy-100/70">
                  {f.desc}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                  {f.highlights.map((h, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-2 text-xs font-bold text-navy-700 dark:text-navy-100"
                    >
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-navy-800 text-white dark:bg-lavender dark:text-navy-900 mt-0.5">
                        <Check className="h-2.5 w-2.5 stroke-[3]" />
                      </span>
                      <span>{h}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-6 flex justify-center">
                <div className="relative overflow-hidden rounded-3xl border border-navy-100 bg-white p-3 shadow-2xl dark:border-white/10 dark:bg-navy-800">
                  <img
                    src={f.media}
                    alt={f.title}
                    className="rounded-2xl max-h-[420px] w-auto object-contain mx-auto"
                    loading="lazy"
                  />
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

export default AIWorkflows;
